import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Item master list.
 *
 * Note what this grid does NOT have a column for: stock on hand. The item row carries no
 * balance — slice 2 refused `current_stock`, `average_cost` and `reserved_stock` precisely
 * because they are ledger projections, and ICRM's `crm_products` carries all three with
 * nothing writing them. On-hand lives on the stock enquiry and availability screens, per
 * warehouse, where it is a real number.
 */
import { Box, Button, Chip, MenuItem, Stack, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { useItems, useUomIndex } from '@inai/inventory-core/react';
import { ErrorNotice } from '../components/Feedback.js';
import { ScreenHeader } from '../components/Layout.js';
import { RateText } from '../components/Numbers.js';
import { NewItemDialog } from './NewItemDialog.js';
import { ItemDetailScreen } from './ItemDetailScreen.js';
const ITEM_TYPES = [
    { value: 'ALL', label: 'All types' },
    { value: 'STOCK', label: 'Stock' },
    { value: 'GOODS', label: 'Goods' },
    { value: 'SERVICE', label: 'Service' },
    { value: 'NON_STOCK', label: 'Non-stock' },
    { value: 'BUNDLE', label: 'Bundle' },
];
export function ItemListScreen({ onOpenItem }) {
    const [search, setSearch] = useState('');
    const [itemType, setItemType] = useState('ALL');
    const [status, setStatus] = useState('ACTIVE');
    const [creating, setCreating] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const filter = useMemo(() => ({
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(itemType !== 'ALL' ? { item_type: itemType } : {}),
        status,
        limit: 200,
    }), [search, itemType, status]);
    const { data: items = [], isLoading, error } = useItems(filter);
    const uoms = useUomIndex();
    const columns = useMemo(() => [
        { field: 'item_code', headerName: 'Code', width: 130 },
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
        {
            field: 'item_type',
            headerName: 'Type',
            width: 120,
            renderCell: (params) => (_jsx(Chip, { size: "small", variant: "outlined", label: params.row.item_type, color: params.row.is_stock_tracked ? 'info' : 'default' })),
        },
        {
            field: 'base_uom_id',
            headerName: 'UOM',
            width: 90,
            valueGetter: (_v, row) => uoms.get(row.base_uom_id)?.code ?? '—',
        },
        {
            field: 'tracking_policy',
            headerName: 'Tracking',
            width: 110,
            renderCell: (params) => params.row.tracking_policy === 'NONE' ? (_jsx(Box, { component: "span", sx: { color: 'text.disabled' }, children: "\u2014" })) : (_jsx(Chip, { size: "small", label: params.row.tracking_policy, variant: "outlined" })),
        },
        { field: 'valuation_method', headerName: 'Valuation', width: 105 },
        {
            field: 'standard_cost',
            headerName: 'Standard cost',
            width: 140,
            align: 'right',
            headerAlign: 'right',
            // NOT `type: 'number'`. A numeric column would coerce the value through a JS
            // number to sort and format it, which is exactly what invariant 5 forbids. The
            // cell renders from the exact Dec; sorting uses the same exact comparison.
            sortComparator: (a, b) => a.cmp(b),
            renderCell: (params) => _jsx(RateText, { value: params.row.standard_cost }),
        },
        { field: 'hsn_code', headerName: 'HSN', width: 100 },
    ], [uoms]);
    if (selectedId && !onOpenItem) {
        return _jsx(ItemDetailScreen, { itemId: selectedId, onBack: () => setSelectedId(null) });
    }
    return (_jsxs(Box, { children: [_jsx(ScreenHeader, { title: "Items", subtitle: "One item master per tenant. A product created in CRM is the same record here.", actions: _jsx(Button, { variant: "contained", size: "small", onClick: () => setCreating(true), children: "New item" }) }), _jsxs(Stack, { direction: { xs: 'column', sm: 'row' }, spacing: 1.5, sx: { mb: 2 }, children: [_jsx(TextField, { size: "small", label: "Search", placeholder: "Name, code or HSN", value: search, onChange: (e) => setSearch(e.target.value), sx: { flex: 1, minWidth: 220 } }), _jsx(TextField, { select: true, size: "small", label: "Type", value: itemType, onChange: (e) => setItemType(e.target.value), sx: { width: 170 }, children: ITEM_TYPES.map((t) => (_jsx(MenuItem, { value: t.value, children: t.label }, t.value))) }), _jsx(TextField, { select: true, size: "small", label: "Status", value: status, onChange: (e) => setStatus(e.target.value), sx: { width: 150 }, children: ['ACTIVE', 'INACTIVE', 'ARCHIVED'].map((s) => (_jsx(MenuItem, { value: s, children: s }, s))) })] }), error ? _jsx(ErrorNotice, { error: error, sx: { mb: 2 } }) : null, _jsx(DataGrid, { rows: items, columns: columns, loading: isLoading, getRowId: (row) => row.id, disableRowSelectionOnClick: true, onRowClick: (params) => {
                    if (onOpenItem)
                        onOpenItem(params.row.id);
                    else
                        setSelectedId(params.row.id);
                }, initialState: { pagination: { paginationModel: { pageSize: 25 } } }, pageSizeOptions: [25, 50, 100], density: "compact", sx: {
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    '& .MuiDataGrid-row': { cursor: 'pointer' },
                } }), _jsx(NewItemDialog, { open: creating, onClose: () => setCreating(false), onCreated: setSelectedId })] }));
}
//# sourceMappingURL=ItemListScreen.js.map